// openEHR RM model — BMM package org.openehr.rm.common.resource
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { CODE_PHRASE } from "../data_types/text.ts";
import type { REVISION_HISTORY } from "./generic.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * Abstract idea of an online resource created by a human author.
 */
export abstract class AUTHORED_RESOURCE {
  /**
   * Language in which this resource was initially authored. Although there is no language primacy of resources overall, the language of original authoring is required to ensure natural language translations can preserve quality. Language is relevant in both the description and ontology sections.
   */
  original_language?: CODE_PHRASE;
  /**
   * Internal storage for is_controlled
   * @protected
   */
  protected _is_controlled?: openehr_base.Boolean;

  /**
   * True if this resource is under any kind of change control (even file copying), in which case revision history is created.
   */
  get is_controlled(): boolean | undefined {
    return this._is_controlled?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_controlled.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_controlled(): openehr_base.Boolean | undefined {
    return this._is_controlled;
  }

  /**
   * Sets is_controlled from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_controlled(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_controlled = undefined;
    } else if (typeof val === "boolean") {
      this._is_controlled = openehr_base.Boolean.from(val);
    } else {
      this._is_controlled = val;
    }
  }

  /**
   * List of details for each natural-language translation made of this resource, keyed by language. For each translation listed here, there must be corresponding sections in all language-dependent parts of the resource. The \`_original_language_\` does not appear in this list.
   */
  translations?: TRANSLATION_DETAILS[];
  /**
   * Description and lifecycle information of the resource.
   */
  description?: RESOURCE_DESCRIPTION;
  /**
   * The revision history of the resource. Only required if \`_is_controlled_ = True\` (avoids large revision histories for informal or private editing situations).
   */
  revision_history?: REVISION_HISTORY;
  /**
   * Most recent revision in \`_revision_history_\` if \`_is_controlled_\` else  (uncontrolled) .
   * @returns Result value
   */
  current_revision(): openehr_base.String {
    if (!this.is_controlled || !this.revision_history) {
      return openehr_base.String.from("(uncontrolled)");
    }
    return this.revision_history.most_recent_version();
  }

  /**
   * Total list of languages available in this resource, derived from \`_original_language_\` and \`_translations_\`.
   *
   * @returns Result value
   */
  languages_available(): openehr_base.String[] {
    const languages: openehr_base.String[] = [];
    
    // Add original language
    if (this.original_language?.code_string) {
      languages.push(openehr_base.String.from(this.original_language.code_string));
    }
    
    // Add translation languages
    if (this.translations && Array.isArray(this.translations)) {
      for (const translation of this.translations) {
        if ((translation as any).language?.code_string) {
          languages.push(openehr_base.String.from((translation as any).language.code_string));
        }
      }
    }
    
    return languages;
  }
}

/**
 * Defines the descriptive meta-data of a resource.
 */
export class RESOURCE_DESCRIPTION {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Original author of this resource, with all relevant details, including organisation.
   */
  original_author?: undefined;
  /**
   * Other contributors to the resource, probably listed in  \`'name <email>'\`  form.
   */
  other_contributors?: undefined;
  /**
   * Internal storage for lifecycle_state
   * @protected
   */
  protected _lifecycle_state?: openehr_base.String;

  /**
   * Lifecycle state of the resource, typically including states such as: \`initial | submitted | experimental | awaiting_approval | approved | superseded | obsolete\`.
   */
  get lifecycle_state(): string | undefined {
    return this._lifecycle_state?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for lifecycle_state.
   * Use this to access openehr_base.String methods.
   */
  get $lifecycle_state(): openehr_base.String | undefined {
    return this._lifecycle_state;
  }

  /**
   * Sets lifecycle_state from either a primitive value or openehr_base.String wrapper.
   */
  set lifecycle_state(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._lifecycle_state = undefined;
    } else if (typeof val === "string") {
      this._lifecycle_state = openehr_base.String.from(val);
    } else {
      this._lifecycle_state = val;
    }
  }

  /**
   * Internal storage for resource_package_uri
   * @protected
   */
  protected _resource_package_uri?: openehr_base.String;

  /**
   * URI of package to which this resource belongs.
   */
  get resource_package_uri(): string | undefined {
    return this._resource_package_uri?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for resource_package_uri.
   * Use this to access openehr_base.String methods.
   */
  get $resource_package_uri(): openehr_base.String | undefined {
    return this._resource_package_uri;
  }

  /**
   * Sets resource_package_uri from either a primitive value or openehr_base.String wrapper.
   */
  set resource_package_uri(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._resource_package_uri = undefined;
    } else if (typeof val === "string") {
      this._resource_package_uri = openehr_base.String.from(val);
    } else {
      this._resource_package_uri = val;
    }
  }

  /**
   * Additional non language-senstive resource meta-data, as a list of name/value pairs.
   */
  other_details?: undefined;
  /**
   * Reference to owning resource.
   */
  parent_resource?: AUTHORED_RESOURCE;
  /**
   * Details of all parts of resource description that are natural language-dependent, keyed by language code.
   */
  details?: undefined;
}

/**
 * Class providing details of a natural language translation.
 */
export class TRANSLATION_DETAILS {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Language of the translation.
   */
  language?: CODE_PHRASE;
  /**
   * Translator name and other demographic details.
   */
  author?: undefined;
  /**
   * Internal storage for accreditaton
   * @protected
   */
  protected _accreditaton?: openehr_base.String;

  /**
   * Accreditation of translator, usually a national translator's registration or association membership id.
   */
  get accreditaton(): string | undefined {
    return this._accreditaton?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for accreditaton.
   * Use this to access openehr_base.String methods.
   */
  get $accreditaton(): openehr_base.String | undefined {
    return this._accreditaton;
  }

  /**
   * Sets accreditaton from either a primitive value or openehr_base.String wrapper.
   */
  set accreditaton(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._accreditaton = undefined;
    } else if (typeof val === "string") {
      this._accreditaton = openehr_base.String.from(val);
    } else {
      this._accreditaton = val;
    }
  }

  /**
   * Any other meta-data.
   */
  other_details?: undefined;
}

/**
 * Language-specific detail of resource description. When a resource is translated for use in another language environment, each \`RESOURCE_DESCRIPTION_ITEM\` needs to be copied and translated into the new language.
 */
export class RESOURCE_DESCRIPTION_ITEM {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * The localised language in which the items in this description item are written. Coded from openEHR code set \`languages\`.
   */
  language?: CODE_PHRASE;
  /**
   * Internal storage for purpose
   * @protected
   */
  protected _purpose?: openehr_base.String;

  /**
   * Purpose of the resource.
   */
  get purpose(): string | undefined {
    return this._purpose?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for purpose.
   * Use this to access openehr_base.String methods.
   */
  get $purpose(): openehr_base.String | undefined {
    return this._purpose;
  }

  /**
   * Sets purpose from either a primitive value or openehr_base.String wrapper.
   */
  set purpose(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._purpose = undefined;
    } else if (typeof val === "string") {
      this._purpose = openehr_base.String.from(val);
    } else {
      this._purpose = val;
    }
  }

  /**
   * Keywords which characterise this resource, used e.g. for indexing and searching.
   */
  keywords?: undefined;
  /**
   * Internal storage for use
   * @protected
   */
  protected _use?: openehr_base.String;

  /**
   * Description of the uses of the resource, i.e. contexts in which it could be used.
   */
  get use(): string | undefined {
    return this._use?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for use.
   * Use this to access openehr_base.String methods.
   */
  get $use(): openehr_base.String | undefined {
    return this._use;
  }

  /**
   * Sets use from either a primitive value or openehr_base.String wrapper.
   */
  set use(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._use = undefined;
    } else if (typeof val === "string") {
      this._use = openehr_base.String.from(val);
    } else {
      this._use = val;
    }
  }

  /**
   * Internal storage for misuse
   * @protected
   */
  protected _misuse?: openehr_base.String;

  /**
   * Description of any misuses of the resource, i.e. contexts in which it should not be used.
   */
  get misuse(): string | undefined {
    return this._misuse?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for misuse.
   * Use this to access openehr_base.String methods.
   */
  get $misuse(): openehr_base.String | undefined {
    return this._misuse;
  }

  /**
   * Sets misuse from either a primitive value or openehr_base.String wrapper.
   */
  set misuse(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._misuse = undefined;
    } else if (typeof val === "string") {
      this._misuse = openehr_base.String.from(val);
    } else {
      this._misuse = val;
    }
  }

  /**
   * Internal storage for copyright
   * @protected
   */
  protected _copyright?: openehr_base.String;

  /**
   * Optional copyright statement for the resource as a knowledge resource.
   */
  get copyright(): string | undefined {
    return this._copyright?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for copyright.
   * Use this to access openehr_base.String methods.
   */
  get $copyright(): openehr_base.String | undefined {
    return this._copyright;
  }

  /**
   * Sets copyright from either a primitive value or openehr_base.String wrapper.
   */
  set copyright(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._copyright = undefined;
    } else if (typeof val === "string") {
      this._copyright = openehr_base.String.from(val);
    } else {
      this._copyright = val;
    }
  }

  /**
   * URIs of original clinical document(s) or description of which resource is a formalisation, in the language of this description item; keyed by meaning.
   */
  original_resource_uri?: undefined;
  /**
   * Additional language-senstive resource metadata, as a list of name/value pairs.
   */
  other_details?: undefined;
}
